import {
	type ProethicaTestKind,
	getProethicaModuleNumber,
} from "@constants/demo/proethica-tests";
import type { AppTheme } from "@hooks/useAppTheme";
import type { MixedItemTag } from "@memorang/types";
import type { EXAM_STATUS } from "types/tag";
import type {
	ProethicaTestCompletionSignals,
	getProethicaModuleListProps,
} from "./proethica-module-list";

type ProethicaModuleProp = ReturnType<typeof getProethicaModuleListProps>;

export type ProethicaScoreRowData = {
	id: string;
	moduleNumber?: number;
	title: string;
	status: EXAM_STATUS;
	disabled: boolean;
	cta: string;
	scorePercent: number | null;
	thumbnailUrl: string;
	thumbnailSource?: string | number | { uri: string };
	isTest: boolean;
	testKind?: ProethicaTestKind;
	icon?: string;
};

export type ProethicaProgressData = {
	completion: {
		doneCount: number;
		totalCount: number;
		remaining: number;
		allDone: boolean;
		preTestCompleted: boolean;
		postTestPassed: boolean;
	};
	scoreRows: ProethicaScoreRowData[];
};

export const PROETHICA_KNOWLEDGE_CHECK_TOTAL = 5;

export const getKnowledgeCheckCorrectCount = (
	scorePercent: number,
	total = PROETHICA_KNOWLEDGE_CHECK_TOTAL,
) => Math.min(total, Math.max(0, Math.round((scorePercent / 100) * total)));

const SCORE_BAND_HIGH = 80;
const SCORE_BAND_MEDIUM = 65;

export const getScoreBandColor = (score: number, theme: AppTheme): string => {
	if (score >= SCORE_BAND_HIGH) {
		return theme.colors.progress.high;
	}
	if (score >= SCORE_BAND_MEDIUM) {
		return theme.colors.progress.medium;
	}
	return theme.colors.progress.low;
};

// Scores live on the topic-mastery `tags` array keyed by "Module N" label, a
// separate roster from the exam-driven module props. Module 1 (0 items) has no
// tag, so its score stays null and the row renders a status state instead.
const buildScoreByModuleNumber = (
	tags: MixedItemTag[],
): Map<number, number> => {
	const scores = new Map<number, number>();
	for (const tag of tags) {
		const moduleNumber = getProethicaModuleNumber(tag);
		if (moduleNumber == null) {
			continue;
		}
		scores.set(moduleNumber, Math.round(tag.averageScore ?? 0));
	}
	return scores;
};

export const buildProethicaProgress = (
	moduleProps: ProethicaModuleProp[],
	tags: MixedItemTag[],
	{ preTestCompleted, postTestPassed }: ProethicaTestCompletionSignals,
): ProethicaProgressData => {
	const scoreByModuleNumber = buildScoreByModuleNumber(tags);

	const scoreRows: ProethicaScoreRowData[] = moduleProps.map((module) => ({
		id: module.id,
		moduleNumber: module.moduleNumber,
		title: module.title,
		status: module.status,
		disabled: module.disabled,
		cta: module.cta,
		scorePercent:
			module.moduleNumber != null
				? (scoreByModuleNumber.get(module.moduleNumber) ?? null)
				: null,
		thumbnailUrl: module.thumbnailUrl,
		thumbnailSource: module.thumbnailSource,
		isTest: module.isTest,
		testKind: module.testKind,
		icon: module.icon,
	}));

	const totalCount = moduleProps.length;
	const doneCount = moduleProps.filter(
		(module) => module.status === "COMPLETE",
	).length;

	return {
		completion: {
			doneCount,
			totalCount,
			remaining: Math.max(0, totalCount - doneCount),
			allDone: totalCount > 0 && doneCount === totalCount,
			preTestCompleted,
			postTestPassed,
		},
		scoreRows,
	};
};
